import { encryptCard } from '@mc-alberta/card-encrypter'
import * as i18nData from '@mc-alberta/i18n-data'
import { isAuthenticationRequired } from '@mc-alberta/stark'
import { joinAddressNames, splitAddressNames } from '@mc-alberta/supernova-scripts'
import cards from '@/api/cards'
import unprovisionedProfile from '@/api/unprovisionedProfile'
import * as types from '@/store/mutation-types'
import formatDateData from '@/utils/formatDateData'

/**
 * @fileoverview this file contains our cards module for our Vuex store.
 * Anything that logically relates to card data belongs here.
 */

export const defaultProvisioningData = {
  billingAddress: null,
  status: ''
}

const state = {
  newCard: null,
  cardToken: null,
  cardAuthToken: null,
  cards: [],
  provisioningData: { ...defaultProvisioningData },
  unprovisionedCards: []
}

const getters = {
  /**
   * Returns sorted cards by date last used.
   *
   * @param {Object} state - the current state (of cards)
   * @returns {Array} the sorted cards
   */
  sortedCards(state) {
    const sorter = (a, b) => b.lastUsedTime.getTime() - a.lastUsedTime.getTime()
    return [...state.cards].sort(sorter)
  },

  /**
   * Returns oldest card by date created.
   *
   * @param {Object} state - the current state (of cards)
   * @returns {Object} the oldest card
   */
  oldestActiveCard(state) {
    const sorter = (a, b) => a.createTime.getTime() - b.createTime.getTime()
    const activeCards = state.cards.filter((card) => card.status === 'ACTIVE')
    const sortedActiveCards = activeCards.sort(sorter)
    return sortedActiveCards[0]
  },

  /**
   * Get a card by card id
   * @returns {Function} function that takes and ID and returns a card Object
   */
  cardByCardId(state) {
    return (cardId) => state.cards.find((card) => card.id === cardId)
  },

  /**
   * Check if the card already exists
   * @returns {Boolean} function that takes an ID and returns true or false
   */
  isDuplicateCard(state) {
    return state.cards.some((card) => card.id === state.newCard?.srcDigitalCardId)
  },

  /**
   * Billing address format for the new card
   *
   * @param {Object} state
   * @returns {String}
   */
  newCardAddressFormat({ newCard }) {
    if (typeof newCard?.countryCode !== 'string') return
    const cardCountryConfig = i18nData.getCountryConfig(newCard?.countryCode)
    return cardCountryConfig?.billingAddressFormat
  }
}

const actions = {
  /**
   * Verifies if the user entered the correct cvc and stores the new authorization.
   *
   * @param {Vuex} vuex
   * @param {String} cvc - user-submitted CVC
   */
  async validateCvc({ commit, dispatch }, { cvcCode, cardId }) {
    try {
      const { data } = await cards.validateCvc(
        {
          programId: 'SRC',
          validationData: cvcCode
        },
        cardId
      )
      commit(types.SET_CARD_AUTH_TOKEN, data.authorization)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Remove the unprovisioned profile corresponding to a specific card id
   *
   * @param {Vuex} store
   * @param {String} cardId - card id to fetch the unprovisioned profile
   */
  async forgetUnprovisionedCard({ state, commit, dispatch }, cardId) {
    try {
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, false)
      await unprovisionedProfile.deleteCard(cardId, state.cardToken)

      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
      commit(types.DELETE_UNPROVISIONED_CARD, cardId)
    } catch (error) {
      dispatch('setServiceLayerError', error)
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
    }
  },

  /**
   * Deletes a card.
   *
   * @param {String} cardId
   * @returns {Promise<Object>}
   */
  async deleteCard({ commit, dispatch }, cardId) {
    try {
      await cards.deleteCard(cardId)

      commit(types.DELETE_CARD, cardId)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Enrolls a new card.
   *
   * @param {Vuex} store
   * @param {Object} card
   * @param {String} card.cardholderFullName - cardholder's full name
   * @param {String} card.primaryAccountNumber - card PAN number
   * @param {String} card.panExpiration - expiration date, in MMYY format
   * @param {String} [card.cardSecurityCode] - verification code
   */
  async enrollCard({ commit, dispatch }, card) {
    try {
      const { cardSecurityCode, primaryAccountNumber } = card
      const panExpiration = card.panExpiration?.split('/') || []
      const cardData = {
        primaryAccountNumber,
        panExpirationMonth: panExpiration[0],
        panExpirationYear: panExpiration[1],
        cardSecurityCode
      }

      const encryptedCard = await encryptCard({
        cardData,
        keyId: process.env.MASTERCARD_SRC_PAN_ENCRYPTION_KEY_KID,
        keysURL: process.env.MASTERCARD_SRC_KEYS_URL
      })

      const { data } = await cards.addCard({ encryptedCard })
      if (isAuthenticationRequired(data.maskedCard)) {
        dispatch('initializeAuthentication', {
          maskedCard: data.maskedCard,
          cardToken: data.authorization
        })
      }
      commit(types.SET_NEW_CARD_DATA, {
        token: data.authorization,
        card: data.maskedCard
      })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Update card data - edit or add billing address
   * @param {String} data.updatedAddress
   * @param {String} data.cardId
   */
  async updateBillingAddress({ state, commit, dispatch }, data) {
    let {
      firstName,
      lastName,
      city,
      state: subdivision,
      zip,
      ...address
    } = data.updatedAddress || {}

    const cardIndex = state.cards.findIndex((card) => card.id === data.cardId)
    const billingAddress = state.cards[cardIndex]?.billingAddress
    if (billingAddress?.fullName) {
      ;({ firstName, lastName } = splitAddressNames({ name: billingAddress.fullName }) || {})
    }
    const { name } = joinAddressNames({ firstName, lastName })

    const payload = {
      address: {
        ...data.updatedAddress,
        firstName,
        lastName,
        name
      },
      cardId: data.cardId
    }
    const nonEmvcoAddress = {
      ...address,
      fullName: name,
      cityTown: city,
      subdivision,
      postalCode: zip
    }
    try {
      await cards.manageCard(payload)
      commit(types.UPDATE_BILLING_ADDRESS, {
        cardId: data.cardId,
        updatedAddress: nonEmvcoAddress
      })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * resets this module back to default state
   * @param {Vuex} store
   */
  reset({ commit }) {
    commit(types.RESET_PROVISIONING_DATA)
    commit(types.SET_CARDS, undefined)
    commit(types.SET_CARD_AUTH_TOKEN, null)
    commit(types.SET_UNPROVISIONED_CARDS)
  },

  clearNewCardData({ commit }) {
    commit(types.DELETE_NEW_CARD_DATA)
    commit(types.RESET_PROVISIONING_DATA)
  }
}

const mutations = {
  /**
   * Removes the card having the given id from the state.
   *
   * @param {Object} state
   * @param {String} idToDelete
   */
  [types.DELETE_CARD](state, idToDelete) {
    const cardIndex = state.cards.findIndex((card) => card.id === idToDelete)

    if (cardIndex > -1) {
      state.cards.splice(cardIndex, 1)
    }
  },

  /**
   * Pushes the given list of cards onto the state.
   *
   * @param {Object} state
   * @param {Object[]} cards
   */
  [types.SET_CARDS](state, cards = []) {
    // clears out the array and triggers Vue reactivity without reassigning the reference
    state.cards.splice(0)

    cards.forEach((card) => {
      state.cards.push(formatCardData(card))
    })
  },

  /**
   * Pushes the new card into the card list,
   * transforming it from an EMVCO spec card to non EMVCO spec
   *
   * @param {Object} state
   */
  [types.ADD_NEW_CARD](state) {
    const card = transformEmvcoToMastercard(state.newCard)
    state.cards.push(formatCardData(card))
  },

  [types.SET_NEW_CARD_DATA](state, { token, card }) {
    if (token) {
      state.cardToken = token
    }
    if (card) {
      state.newCard = card
    }
  },

  /**
   * Set the CVC-authenticated card token
   *
   * @param {Object} state
   * @param {String} token
   */
  [types.SET_CARD_AUTH_TOKEN](state, token) {
    state.cardAuthToken = token
  },

  /**
   * Removes the new card data from the store
   *
   * @param {Object} state
   */
  [types.DELETE_NEW_CARD_DATA](state) {
    state.newCard = undefined
    state.cardToken = undefined
  },

  /**
   *
   * @param {Object} state
   * @param {String} data.cardId - cardId to update
   * @param {String} data.address - updated non-emvco address
   */
  [types.UPDATE_BILLING_ADDRESS](state, { cardId, updatedAddress }) {
    const cardIndex = state.cards.findIndex((card) => card.id === cardId)

    const { createTime, fullName, id, lastUsedTime } = state.cards[cardIndex]?.billingAddress || {}
    state.cards[cardIndex].billingAddress = {
      createTime,
      fullName,
      id,
      lastUsedTime,
      ...updatedAddress
    }
  },

  /**
   * Set the provisioning data for the (selected) card of interest
   *
   * @param {Object} state
   * @param {Object} payload
   * @param {Object} payload.billingAddress - billing address to bind to card
   * @param {('UNPROVISIONED'|'PROVISIONED')} payload.status - If the card is UNPROVISIONED or PROVISIONED
   */
  [types.SET_PROVISIONING_DATA](state, { billingAddress, status }) {
    state.provisioningData.billingAddress = billingAddress || {
      ...defaultProvisioningData.billingAddress
    }
    state.provisioningData.status = status || defaultProvisioningData.status
  },

  [types.RESET_PROVISIONING_DATA](state) {
    state.provisioningData = { ...defaultProvisioningData }
  },

  /**
   * Stores the unprovisioned cards
   *
   * @param {Object} state
   * @param {Array} unprovisionedCards
   */
  [types.SET_UNPROVISIONED_CARDS](state, unprovisionedCards = []) {
    // clears out the array and triggers Vue reactivity without reassigning the reference
    state.unprovisionedCards.splice(0)

    unprovisionedCards.forEach((card) => {
      state.unprovisionedCards.push(formatCardData(card))
    })
  },

  /**
   * Stores the unprovisioned cards
   *
   * @param {Object} state
   * @param {String} cardId
   */
  [types.DELETE_UNPROVISIONED_CARD](state, cardId) {
    const profileIndex = state.unprovisionedCards.findIndex((card) => {
      return card.id === cardId
    })

    if (profileIndex > -1) {
      state.unprovisionedCards.splice(profileIndex, 1)
    }
  }
}

function formatCardData(card) {
  // format the expiration
  card.fpanExpiration = card.fpanExpiration.replace(/(\d{2})(\d{2})/, '$1/$2')

  return formatDateData(card)
}

/**
 * Transform card spec from emvco standard to mastercard standard
 *
 * Precheckout backend / Checkout use emvco mainly
 * AMS backend uses mastercard mainly
 *
 * @param {Object} emvco - card in emvco format
 *
 * @returns {Object} - card in mastercard format
 */
export function transformEmvcoToMastercard(emvco) {
  return {
    id: emvco.srcDigitalCardId,
    primaryAccountNumberSuffix: emvco.panLastFour,
    paymentNetworkName: emvco.paymentCardDescriptor,
    artUrl: emvco.digitalCardData.artUri,
    status: emvco.digitalCardStatus,
    tokenSuffix: emvco.tokenLastFour,
    issuerName: emvco.issuerName,
    shortProductDescription: emvco.digitalCardData.descriptorName,
    fpanExpiration: `${emvco.panExpirationMonth}${emvco.panExpirationYear}`,
    createTime: emvco.dateOfCardCreated,
    lastUsedTime: emvco.dateOfCardLastUsed,
    countryCode: emvco.countryCode,
    billingAddress: {
      ...emvco.billingAddress,
      id: emvco.billingAddress?.billingAddressId,
      subdivision: emvco.billingAddress?.state,
      postalCode: emvco.billingAddress?.zip,
      cityTown: emvco.billingAddress?.city,
      fullName: emvco.billingAddress?.name
    }
  }
}

/**
 * Transform Mastercard to emvco standard billing address
 *
 * @param {Object} emvco - card in mastercard format
 * @returns {Object} - card in emvco format
 */
export function transformMastercardToEmvcoAddress(mastercard) {
  return {
    line1: mastercard?.line1,
    line2: mastercard?.line2,
    line3: mastercard?.line3,
    city: mastercard?.cityTown,
    state: mastercard?.subdivision,
    countryCode: mastercard?.countryCode,
    zip: mastercard?.postalCode
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
